import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { tap } from 'rxjs';

import { DprAttachmentsGridComponent } from '..';
import { GridApi, GridSync } from '@grid/models';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { FileUploadConfig, NamingConvention } from '@shared/models';
import { NamingConventionService } from '@shared/services/dpr';
import { Constants } from '@shared/utils';
import {
  EditableComponent,
  OnlineDependentComponent,
} from '@dpr-details/models/component';

@UntilDestroy()
@Component({
  selector: 'app-dpr-attachments',
  templateUrl: './dpr-attachments.component.html',
  styleUrls: ['./dpr-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprAttachmentsComponent
  implements EditableComponent, OnlineDependentComponent, OnChanges
{
  //#region Inputs

  @Input({ required: true }) gridData: DprAttachmentsGridData[];
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) isAppOnline: boolean;
  @Input({ required: true }) canEdit: boolean;
  @Input() predefinedChanges: GridSync<DprAttachmentsGridData>;
  @Input() namingConvention: NamingConvention;
  @Input() identifier: string;
  @Input() fileUploadConfig: FileUploadConfig =
    Constants.AttachmentFileUploadConfig;
  @Input() shouldDisplayPrintInReport = true;
  @Input() shouldDisplayPlaceholderCounter = true;
  @Input() enableStickyDirective = true;

  //#endregion

  //#region Outputs

  @Output() gridRegistered = new EventEmitter<
    GridApi<DprAttachmentsGridData>
  >();

  @Output() dataChanged = new EventEmitter();

  //#endregion

  //#region Templates

  @ViewChild(DprAttachmentsGridComponent)
  dprAttachmentsGrid!: DprAttachmentsGridComponent;

  //#endregion

  constructor(
    private cdr: ChangeDetectorRef,
    private namingConventionService: NamingConventionService
  ) {}

  get isNameConventionEnabled(): boolean {
    return this.isAppendix
      ? this.namingConventionService.isRenameAppendixFileEnabled(
          this.namingConvention
        )
      : this.namingConventionService.isRenameAttachmentFileEnabled(
          this.namingConvention
        );
  }

  get isAppendix(): boolean {
    return this.identifier === 'appendix';
  }

  //#region Lifecycle hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canEdit || changes.isAppOnline) {
      this.cdr.markForCheck();
    }
  }

  //#endregion

  onAttachmentsUploaded(dprAttachments: DprAttachmentsGridData[]): void {
    this.dprAttachmentsGrid.api.addRows(...dprAttachments);

    this.dataChanged.emit();
    this.cdr.detectChanges();
  }

  onGridRegistered(gridApi: GridApi<DprAttachmentsGridData>): void {
    this.gridRegistered.emit(gridApi);

    gridApi.isDirty$
      .pipe(
        untilDestroyed(this),
        tap(() => this.dataChanged.emit())
      )
      .subscribe();
  }
}
